import { useState } from "react"
import { errorToast } from "@/v2-ui/toast"
import { useTenantCustomerAddManyMutation } from "@/v2-backoffice-client/tenant/customer/add/many/tenant.customer.add.many.mutation"
import { useUserCustomerUpdateManyMutation } from "@/v2-backoffice-client/user/customer/update/many/user.customer.update.many.mutation"
import type { MismatchPlan } from "@/v2-backoffice-client/user/customerMismatch/userCustomerMismatch.utils"

/**
 * Runs a bulk plan from the Customer Mismatches board: one team relink per
 * target customer first, then one user move per target customer, so a
 * user always lands on the customer of the team they sit in. Stops at the
 * first write that fails (toasted) and reports false, so the caller leaves
 * the selection and the rows in place for the next look.
 */
export function useUserCustomerMismatchPlanRunner() {
  const [isRunning, setIsRunning] = useState(false)
  const [assignTeams, { error: teamError }] = useTenantCustomerAddManyMutation()
  const [updateUsers, { error: userError }] =
    useUserCustomerUpdateManyMutation()
  errorToast(teamError, { title: "Failed to update the teams' customer" })
  errorToast(userError, { title: "Failed to update the users' customer" })

  async function run(plan: MismatchPlan) {
    setIsRunning(true)
    try {
      // errorPolicy is "all" app-wide: a rejected write resolves with the
      // error (toasted above) instead of throwing.
      for (const move of plan.teamMoves) {
        if (!move.target.ExternalCustomerId) continue
        const result = await assignTeams({
          variables: {
            tenantIds: move.tenantIds,
            externalCustomerId: move.target.ExternalCustomerId,
          },
        })
        if (
          result.errors?.length ||
          !result.data?.assignCustomerToManyTenants?.Success
        ) {
          return false
        }
      }
      for (const move of plan.userMoves) {
        const result = await updateUsers({
          variables: { ids: move.userIds, customerId: move.target.Id },
        })
        if (result.errors?.length || !result.data?.setManyUsersCustomer) {
          return false
        }
      }
      return true
    } finally {
      setIsRunning(false)
    }
  }

  return { run, isRunning }
}
